import { useToast } from '@lifebit-ai/lds'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { extractErrorMessage } from 'core/utils'
import { rejectImportRequest } from 'services/import-requests'
import { ImportRequestQueryKeys } from 'services/import-requests/queryKeys'
import { invalidateRequestActivityQueries } from 'services/request-activity/invalidateRequestActivityQueries'
import type { ApiErrorResponse } from 'services/types'

export interface RejectImportRequestParams {
  reason: string
}

interface UseRejectImportRequestParams {
  onSuccess?: () => void
  requestId: string
}

export const useRejectImportRequest = ({ onSuccess, requestId }: UseRejectImportRequestParams) => {
  const toast = useToast()
  const queryClient = useQueryClient()

  return useMutation<void, ApiErrorResponse, RejectImportRequestParams>({
    mutationFn: ({ reason }) => rejectImportRequest(requestId, reason),
    onSuccess: () => {
      toast({ message: 'Import request rejected successfully', type: 'success' })
      queryClient.invalidateQueries({ queryKey: ImportRequestQueryKeys.Verification(requestId) })
      queryClient.invalidateQueries({ queryKey: ImportRequestQueryKeys.All() })
      invalidateRequestActivityQueries(queryClient)
      onSuccess?.()
    },
    onError: error => {
      toast({ message: extractErrorMessage(error, 'Failed to reject import request'), type: 'error' })
    }
  })
}
